import React, { useEffect, useMemo, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { AlertCircle, AlertTriangle, CheckCircle, Edit, FileText, Plus, Printer, Settings, Shield, Trash2, X } from 'lucide-react';
import { Badge } from './ui/badge';
import SortableEntryList from './SortableEntryList';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ScenarioModule = ({ theme, exerciseId, exerciseName }) => {
  const [scenarioData, setScenarioData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('response');

  // Helper function to format timestamp in 24-hour format (e.g., "2024-10-27 23:15")
  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    category: 'response',
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    timestamp: '',
    location: '',
    contact_info: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [locations, setLocations] = useState([]);

  // Fetch map markers as locations - filtered by current exercise
  React.useEffect(() => {
    const fetchMapMarkers = async () => {
      if (!exerciseId) return; // Don't fetch if no exercise selected
      try {
        const token = secureStorage.getToken();
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API_BASE_URL}/api/map-objects?type=marker&exercise_id=${exerciseId}`, { headers });
        if (response.ok) {
          const data = await response.json();
          // Map to expected format
          setLocations(data.map(marker => ({ id: marker.id, name: marker.name })));
        }
      } catch (error) {
        console.error('Error fetching map markers:', error);
      }
    };
    fetchMapMarkers();
  }, [exerciseId]);

  // Default location options
  const defaultLocations = [
    'Command Post',
    'Base Camp',
    'Staging Area',
    'Hospital',
    'Fire Station',
    'Police Station',
    'Emergency Operations Center',
    'Field Office',
    'Other'
  ];

  // Dynamic location options - combine default and custom locations
  const locationOptions = React.useMemo(() => {
    let options = [...defaultLocations];
    
    // Add custom locations from database
    if (locations.length > 0) {
      const customLocationNames = locations.map(location => location.name);
      options = [...options, ...customLocationNames.filter(name => !options.includes(name))];
    }
    
    // Ensure the current location value is included if editing
    if (editingEntry && editingEntry.location && !options.includes(editingEntry.location)) {
      options = [editingEntry.location, ...options];
    }
    
    return options;
  }, [locations, editingEntry?.location]);

  // Emergency phases and their fields
  const categories = {
    'prevention': {
      label: 'Prevention',
      color: 'text-green-500',
      icon: Shield,
      fields: []
    },
    'mitigation': {
      label: 'Mitigation',
      color: 'text-yellow-500',
      icon: Settings,
      fields: []
    },
    'preparedness': {
      label: 'Preparedness',
      color: 'text-orange-500',
      icon: AlertTriangle,
      fields: []
    },
    'response': {
      label: 'Response',
      color: 'text-red-500',
      icon: AlertCircle,
      fields: []
    },
    'recovery': {
      label: 'Recovery',
      color: 'text-green-500',
      icon: CheckCircle,
      fields: []
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-500' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-500' }
  ];

  // Load scenario data on component mount
  useEffect(() => {
    loadScenarioData();
  }, [exerciseId]);

  const loadScenarioData = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/scenario-entries?exercise_id=${exerciseId}`);
      setScenarioData(response.data);
    } catch (error) {
      console.error('Error loading scenario data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: activeCategory,
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      timestamp: new Date().toISOString().slice(0, 16),
      location: '',
      contact_info: '',
      contact_name: '',
      contact_phone: '',
      notes: ''
    });
    setErrors({});
    setEditingEntry(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const categoryFields = categories[formData.category]?.fields || [];

    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';

    // Validate category-specific required fields
    categoryFields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const entryData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: formData.timestamp || new Date().toISOString()
      };

      if (editingEntry) {
        await axios.put(`${API}/scenario-entries/${editingEntry.id}`, entryData);
      } else {
        await axios.post(`${API}/scenario-entries`, entryData);
      }

      resetForm();
      setShowForm(false);
      loadScenarioData();
    } catch (error) {
      console.error('Error saving scenario entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category || 'response',
      title: entry.title || '',
      description: entry.description || '',
      priority: entry.priority || 'medium',
      status: entry.status || 'active',
      timestamp: entry.timestamp ? entry.timestamp.slice(0, 16) : '',
      location: entry.location || '',
      contact_info: entry.contact_info || '',
      contact_name: entry.contact_name || '',
      contact_phone: entry.contact_phone || '',
      notes: entry.notes || '',
      date_from: entry.date_from || '',
      date_to: entry.date_to || '',
      ...Object.fromEntries(
        Object.keys(categories).flatMap(cat => 
          categories[cat].fields.map(field => [field.key, entry[field.key] || ''])
        )
      )
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.delete(`${API}/scenario-entries/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadScenarioData();
      alert('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting scenario entry:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete this entry. Only Agency Admins and Super Admins can delete entries.');
      } else if (error.response?.status === 404) {
        alert('Entry not found. It may have already been deleted.');
      } else {
        alert('Failed to delete entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (entry = null) => {
    if (entry) {
      // Single entry print (unchanged)
      const printContent = generateSingleEntryPrint(entry);
      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, `
        <html>
          <head>
            <title>ICS 201 - Incident Briefing Entry</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
              .entry { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; }
              .field { margin-bottom: 8px; }
              .label { font-weight: bold; display: inline-block; width: 150px; }
              .category { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-left: 4px solid #f59e0b; }
              .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
              @media print { .entry { page-break-inside: avoid; } }
            </style>
          </head>
          <body>
            ${printContent}
            <div class="footer">
              <p>Generated on ${new Date().toLocaleString()}</p>
              <p>Powered by EXRSIM - Emergency Response Simulator</p>
            </div>
          </body>
        </html>
      `);
      printWindow.print();
      return;
    }

    // Print All - Gather data from all Operations submenus
    try {
      const [scenarioResponse, objectivesResponse, safetyResponse, organizationResponse] = await Promise.all([
        axios.get(`${API}/scenario-entries?exercise_id=${exerciseId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/incident-objectives?exercise_id=${exerciseId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/safety-entries?exercise_id=${exerciseId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/participants?exercise_id=${exerciseId}`).catch(() => ({ data: [] }))
      ]);

      const scenarioData = scenarioResponse.data || [];
      const objectivesData = objectivesResponse.data || [];
      const safetyData = safetyResponse.data || [];
      const organizationData = organizationResponse.data || [];

      const printContent = `
        <div class="header">
          <h1>ICS 201 - Incident Briefing</h1>
          <h2>Initial Action Plan</h2>
          <p>Generated on: ${new Date().toLocaleString()}</p>
        </div>

        <!-- Scenario Information -->
        <div class="section">
          <h2>Incident Scenario & Situation</h2>
          ${scenarioData.length > 0 ? scenarioData.map(entry => {
            const category = categories[entry.category] || {};
            return `
              <div class="entry">
                <div class="category">
                  <h3>${category.label || entry.category} - ${entry.title}</h3>
                </div>
                <div class="field"><span class="label">Emergency Phase:</span> ${category.label || entry.category}</div>
                <div class="field"><span class="label">Situation Summary:</span> ${entry.description || 'N/A'}</div>
                <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
                <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
                <div class="field"><span class="label">Timestamp:</span> ${entry.timestamp ? format24HourTimestamp(entry.timestamp) : 'N/A'}</div>
                <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
                <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
                <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
              </div>
            `;
          }).join('') : '<p>No scenario information entered.</p>'}
        </div>

        <!-- Incident Objectives -->
        <div class="section">
          <h2>Incident Objectives & Strategy</h2>
          ${objectivesData.length > 0 ? objectivesData.map(entry => `
            <div class="entry">
              <div class="category">
                <h3>Objective: ${entry.title}</h3>
              </div>
              <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
              <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
              <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
              <div class="field"><span class="label">Assigned To:</span> ${entry.contact_info || 'N/A'}</div>
              <div class="field"><span class="label">Target Date:</span> ${entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : 'N/A'}</div>
            </div>
          `).join('') : '<p>No incident objectives defined.</p>'}
        </div>

        <!-- Safety Information -->
        <div class="section">
          <h2>Safety Considerations & Hazards</h2>
          ${safetyData.length > 0 ? safetyData.map(entry => `
            <div class="entry">
              <div class="category">
                <h3>Safety Item: ${entry.title}</h3>
              </div>
              <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
              <div class="field"><span class="label">Risk Level:</span> ${entry.priority || 'N/A'}</div>
              <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
              <div class="field"><span class="label">Location/Area:</span> ${entry.location || 'N/A'}</div>
              <div class="field"><span class="label">Safety Officer:</span> ${entry.contact_info || 'N/A'}</div>
              <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
            </div>
          `).join('') : '<p>No safety information recorded.</p>'}
        </div>

        <!-- Incident Area Map (if captured) -->
        ${window.currentMapScreenshot ? `
        <div class="section">
          <h2>Incident Area Map & Geographic Information</h2>
          <div class="entry">
            <div class="category">
              <h3>Current Incident Area Map</h3>
            </div>
            <div style="text-align: center; margin: 20px 0;">
              <img src="${window.currentMapScreenshot}" alt="Incident Area Map" style="max-width: 100%; max-height: 500px; border: 2px solid #ddd; border-radius: 5px;" />
            </div>
            <div class="field"><span class="label">Map Source:</span> Planning → Geospatial → Mapping</div>
            <div class="field"><span class="label">Captured:</span> ${window.mapCaptureTimestamp || 'N/A'}</div>
            <div class="field"><span class="label">Notes:</span> Current view of incident area with geographic features, operational boundaries, and map objects.</div>
          </div>
        </div>
        ` : ''}

        <!-- Organization Assignment List -->
        <div class="section">
          <h2>ICS 203 - Organization Assignment List</h2>
          ${(() => {
            // Complete ICS positions structure
            const allPositions = [
              'Command - Incident Commander',
              'Command - Incident Commander (deputy)',
              'Command - Safety Officer',
              'Command - Information Officer',
              'Command - Liaison Officer',
              'Operations - Section Chief',
              'Operations - Air Ops Branch Director',
              'Operations - Branch 1 Director',
              'Operations - Branch 2 Director',
              'Operations - Air Support Group Supervisor',
              'Operations - Helibase Supervisor',
              'Operations - Helispot Supervisor',
              'Operations - Air Tactical Group Supervisor',
              'Operations - Strike Team Leader',
              'Operations - Task Force Leader',
              'Operations - Single Resource Boss',
              'Planning - Section Chief',
              'Planning - Resource Unit Leader',
              'Planning - Situation Unit Leader',
              'Planning - Documentation Unit Leader',
              'Planning - Demobilization Unit Leader',
              'Planning - Technical Specialist',
              'Logistics - Section Chief',
              'Logistics - Support Branch Director',
              'Logistics - Service Branch Director',
              'Logistics - Supply Unit Leader',
              'Logistics - Facilities Unit Leader',
              'Logistics - Ground Support Unit Leader',
              'Logistics - Communications Unit Leader',
              'Logistics - Food Unit Leader',
              'Logistics - Medical Unit Leader',
              'Finance - Section Chief',
              'Finance - Time Unit Leader',
              'Finance - Procurement Unit Leader',
              'Finance - Compensation Claims Unit Leader',
              'Finance - Cost Unit Leader'
            ];
            
            // Function to find participant by position
            const findParticipantByPosition = (position) => {
              return organizationData.find(p => p.position === position && p.involvedInExercise);
            };
            
            // Group positions by section
            const sectionGroups = {
              'Command Staff': allPositions.filter(pos => pos.startsWith('Command -')),
              'Operations Section': allPositions.filter(pos => pos.startsWith('Operations -')),
              'Planning Section': allPositions.filter(pos => pos.startsWith('Planning -')),
              'Logistics Section': allPositions.filter(pos => pos.startsWith('Logistics -')),
              'Finance/Administration Section': allPositions.filter(pos => pos.startsWith('Finance -'))
            };
            
            return Object.entries(sectionGroups).map(([sectionName, positions]) => `
              <div class="entry">
                <div class="category">
                  <h3>${sectionName}</h3>
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 10px;">
                  ${positions.map(position => {
                    const participant = findParticipantByPosition(position);
                    const positionTitle = position.replace(/^[^-]+-\\s*/, ''); // Remove section prefix
                    return `
                      <div style="padding: 8px; border: 1px solid ${participant ? '#10b981' : '#d1d5db'}; border-radius: 4px; background: ${participant ? '#f0fdfa' : '#f9fafb'};">
                        <div style="font-weight: bold; color: #374151; margin-bottom: 4px; font-size: 12px;">${positionTitle}</div>
                        <div style="color: ${participant ? '#059669' : '#6b7280'}; font-size: 11px;">
                          ${participant ? 
                            `<strong>${participant.name || participant.firstName + ' ' + participant.lastName}</strong><br/>
                             ${participant.cellPhone ? 'Phone: ' + participant.cellPhone : ''}${participant.email ? (participant.cellPhone ? '<br/>' : '') + 'Email: ' + participant.email : ''}${participant.agency ? '<br/>Agency: ' + participant.agency : ''}` : 
                            '<em>Unassigned</em>'
                          }
                        </div>
                      </div>
                    `;
                  }).join('')}
                </div>
              </div>
            `).join('');
          })()}
        </div>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, `
        <html>
          <head>
            <title>ICS 201 - Incident Briefing</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; }
              .header h1 { color: #f59e0b; margin-bottom: 5px; }
              .header h2 { color: #666; margin-bottom: 10px; }
              .section { margin-bottom: 40px; page-break-inside: avoid; }
              .section h2 { color: #f59e0b; border-bottom: 2px solid #f59e0b; padding-bottom: 5px; margin-bottom: 20px; }
              .entry { margin-bottom: 25px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; background: #fafafa; }
              .field { margin-bottom: 8px; }
              .label { font-weight: bold; display: inline-block; width: 180px; color: #333; }
              .category { background: #f59e0b; color: white; padding: 10px; margin-bottom: 15px; border-radius: 5px; }
              .category h3 { margin: 0; color: white; }
              .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
              @media print { 
                .entry { page-break-inside: avoid; }
                .section { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            ${printContent}
            <div class="footer">
              <p><strong>ICS 201 - Incident Briefing Report</strong></p>
              <p>Generated on ${new Date().toLocaleString()}</p>
              <p>Powered by EXRSIM - Emergency Response Simulator</p>
            </div>
          </body>
        </html>
      `);
      printWindow.print();
    } catch (error) {
      console.error('Error generating comprehensive print report:', error);
      alert('Error generating report. Please try again.');
    }
  };

  const generateSingleEntryPrint = (entry) => {
    const category = categories[entry.category];
    return `
      <div class="header">
        <h1>Scenario Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
      </div>
      <div class="entry">
        <div class="category">
          <h3>${category?.label || entry.category}</h3>
        </div>
        <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
        <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
        <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
        <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
        <div class="field"><span class="label">Timestamp:</span> ${entry.timestamp ? format24HourTimestamp(entry.timestamp) : 'N/A'}</div>
        <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
        <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
        <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
        ${category?.fields.map(field => 
          `<div class="field"><span class="label">${field.label}:</span> ${entry[field.key] || 'N/A'}</div>`
        ).join('') || ''}
      </div>
    `;
  };

  const generateMultipleEntriesPrint = () => {
    const selectedData = selectedEntries.length > 0 ? 
      scenarioData.filter(entry => selectedEntries.includes(entry.id)) : 
      scenarioData;
    
    return `
      <div class="header">
        <h1>Scenario Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
        <p>${selectedData.length} entry(ies)</p>
      </div>
      ${selectedData.map(entry => generateSingleEntryPrint(entry)).join('')}
    `;
  };

  const handlePrintScenario = () => {
    const printContent = generateMultipleEntriesPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Scenario Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .entry { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 150px; }
            .category { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-left: 4px solid #f59e0b; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print {
              .entry { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const handlePrintIncidentActionPlan = async () => {
    console.log('handlePrintIncidentActionPlan called');
    try {
      setLoading(true);
      console.log('Starting to fetch data...');
      
      // Fetch all data from ALL Operations menu sections
      const [
        scenarioResponse, 
        objectivesResponse, 
        safetyResponse,
        organizationResponse
      ] = await Promise.all([
        axios.get(`${API}/scenario-entries?exercise_id=${exerciseId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/incident-objectives?exercise_id=${exerciseId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/safety-entries?exercise_id=${exerciseId}`).catch(() => ({ data: [] })),
        axios.get(`${API}/participants?exercise_id=${exerciseId}`).catch(() => ({ data: [] }))
      ]);

      const scenarioEntries = scenarioResponse.data || [];
      const objectivesEntries = objectivesResponse.data || [];
      const safetyEntries = safetyResponse.data || [];
      const organizationEntries = organizationResponse.data || [];

      // Generate print content
      const mapScreenshot = window.currentMapScreenshot || null;
      const mapTimestamp = window.mapCaptureTimestamp || null;
      console.log('Map screenshot available:', !!mapScreenshot, 'Timestamp:', mapTimestamp);
      
      const printContent = generateIncidentActionPlanPrint(
        scenarioEntries, 
        objectivesEntries, 
        safetyEntries,
        organizationEntries,
        mapScreenshot,
        mapTimestamp
      );
      
      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, `
        <html>
          <head>
            <title>Incident Action Plan</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; }
              .header h1 { color: #f59e0b; margin-bottom: 5px; }
              .header h2 { color: #666; margin-bottom: 10px; }
              .section { margin-bottom: 40px; page-break-inside: avoid; }
              .section h2 { color: #f59e0b; border-bottom: 2px solid #f59e0b; padding-bottom: 5px; margin-bottom: 20px; }
              .entry { margin-bottom: 25px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; background: #fafafa; }
              .field { margin-bottom: 8px; }
              .label { font-weight: bold; display: inline-block; width: 180px; color: #333; }
              .category { background: #f59e0b; color: white; padding: 10px; margin-bottom: 15px; border-radius: 5px; }
              .category h3 { margin: 0; color: white; }
              .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
              @media print { 
                .entry { page-break-inside: avoid; }
                .section { page-break-inside: avoid; }
              }
            </style>
          </head>
          <body>
            ${printContent}
            <div class="footer">
              <p><strong>Incident Action Plan</strong></p>
              <p>Generated on ${new Date().toLocaleString()}</p>
              <p>Powered by EXRSIM - Emergency Response Simulator</p>
            </div>
          </body>
        </html>
      `);
      printWindow.print();
    } catch (error) {
      console.error('Error generating Incident Action Plan:', error);
      alert('Failed to generate Incident Action Plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateIncidentActionPlanPrint = (scenarioEntries, objectivesEntries, safetyEntries, organizationEntries, mapScreenshot, mapTimestamp) => {
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    const scenarioCategories = {
      'prevention': 'Prevention',
      'mitigation': 'Mitigation',
      'preparedness': 'Preparedness',
      'response': 'Response',
      'recovery': 'Recovery'
    };

    const objectivesCategories = {
      'core-objectives': 'Core Objectives',
      'organizational-principles': 'Organizational Principles',
      'resource-management': 'Resource Management'
    };

    return `
      <div class="header">
        <h1>Incident Action Plan</h1>
        <h2>${exerciseName || 'Exercise Report'}</h2>
        <p>Generated: ${new Date().toLocaleString()}</p>
      </div>

      <!-- Scenario Information Section -->
      <div class="section">
        <h2>Scenario Information</h2>
        ${scenarioEntries.length > 0 ? scenarioEntries.map(entry => `
          <div class="entry">
            <div class="category">
              <h3>${scenarioCategories[entry.category] || entry.category}</h3>
            </div>
            <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
            <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
            <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
            <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
            <div class="field"><span class="label">Timestamp:</span> ${formatTimestamp(entry.timestamp)}</div>
            <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
            <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
            <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
          </div>
        `).join('') : '<p>No scenario entries found.</p>'}
      </div>

      <!-- Incident Objectives Section -->
      <div class="section">
        <h2>Incident Objectives</h2>
        ${objectivesEntries.length > 0 ? objectivesEntries.map(entry => `
          <div class="entry">
            <div class="category">
              <h3>${objectivesCategories[entry.category] || entry.category}</h3>
            </div>
            <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
            <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
            <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
            <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
            <div class="field"><span class="label">Timestamp:</span> ${formatTimestamp(entry.timestamp)}</div>
            ${entry.overall_safety ? `<div class="field"><span class="label">Overall Safety:</span> ${entry.overall_safety}</div>` : ''}
            ${entry.effective_operations ? `<div class="field"><span class="label">Effective Operations:</span> ${entry.effective_operations}</div>` : ''}
            ${entry.adaptive_organization ? `<div class="field"><span class="label">Adaptive Organization:</span> ${entry.adaptive_organization}</div>` : ''}
            ${entry.strategies_for_objectives ? `<div class="field"><span class="label">Strategies:</span> ${entry.strategies_for_objectives}</div>` : ''}
            ${entry.tactical_approaches ? `<div class="field"><span class="label">Tactical Approaches:</span> ${entry.tactical_approaches}</div>` : ''}
            ${entry.standardized_approach ? `<div class="field"><span class="label">Standardized Approach:</span> ${entry.standardized_approach}</div>` : ''}
            ${entry.roles_responsibilities ? `<div class="field"><span class="label">Roles & Responsibilities:</span> ${entry.roles_responsibilities}</div>` : ''}
            ${entry.span_of_control ? `<div class="field"><span class="label">Span of Control:</span> ${entry.span_of_control}</div>` : ''}
            ${entry.modular_organization ? `<div class="field"><span class="label">Modular Organization:</span> ${entry.modular_organization}</div>` : ''}
            ${entry.resource_integration ? `<div class="field"><span class="label">Resource Integration:</span> ${entry.resource_integration}</div>` : ''}
            ${entry.resource_deployment ? `<div class="field"><span class="label">Resource Deployment:</span> ${entry.resource_deployment}</div>` : ''}
          </div>
        `).join('') : '<p>No incident objectives found.</p>'}
      </div>

      <!-- Safety Management Section -->
      <div class="section">
        <h2>Safety Management</h2>
        ${safetyEntries.length > 0 ? safetyEntries.map(entry => `
          <div class="entry">
            <div class="field"><span class="label">Hazard:</span> ${entry.hazard || 'N/A'}</div>
            <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
            <div class="field"><span class="label">Risk Level:</span> ${entry.risk_level || 'N/A'}</div>
            <div class="field"><span class="label">Mitigation:</span> ${entry.mitigation || 'N/A'}</div>
            <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
            <div class="field"><span class="label">Assigned To:</span> ${entry.assigned_to || 'N/A'}</div>
            <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
          </div>
        `).join('') : '<p>No safety management entries found.</p>'}
      </div>

      <!-- Organization List Section -->
      <div class="section">
        <h2>Organization List</h2>
        ${organizationEntries.length > 0 ? organizationEntries.map(entry => `
          <div class="entry">
            <div class="field"><span class="label">Name:</span> ${entry.name || 'N/A'}</div>
            <div class="field"><span class="label">Position:</span> ${entry.position || 'N/A'}</div>
            <div class="field"><span class="label">Agency:</span> ${entry.agency || 'N/A'}</div>
            <div class="field"><span class="label">Contact:</span> ${entry.contact || 'N/A'}</div>
            <div class="field"><span class="label">Email:</span> ${entry.email || 'N/A'}</div>
            <div class="field"><span class="label">Cell Phone:</span> ${entry.cellPhone || 'N/A'}</div>
          </div>
        `).join('') : '<p>No organization entries found.</p>'}
      </div>

      <!-- Incident Area Map Section (if captured) -->
      ${mapScreenshot ? `
      <div class="section">
        <h2>Incident Area Map</h2>
        <div class="entry">
          <div style="text-align: center; margin: 20px 0;">
            <img src="${mapScreenshot}" alt="Incident Area Map" style="max-width: 100%; max-height: 600px; border: 2px solid #ddd; border-radius: 5px;" />
          </div>
          <div class="field"><span class="label">Map Source:</span> ICS Platform → Mapping</div>
          <div class="field"><span class="label">Captured:</span> ${mapTimestamp || 'N/A'}</div>
          <div class="field"><span class="label">Notes:</span> Current view of incident area with geographic features and map objects.</div>
        </div>
      </div>
      ` : ''}
    `;
  };

  const toggleEntrySelection = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
  };

  const filteredData = activeCategory === 'all' 
    ? (scenarioData || []) 
    : (scenarioData || []).filter(entry => entry.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              ICS 201 - Incident Briefing
            </h1>
            <p className="text-white text-sm">
              Manage current situation, response actions, and operational status information
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => handlePrintIncidentActionPlan()}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Incident Action Plan
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
            {scenarioData.length > 0 && (
              <Button
                onClick={() => handlePrintScenario()}
                variant="outline"
                className="border-green-500 text-green-500 hover:bg-green-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Scenario
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <IconComponent className="h-4 w-4 mr-2" />
                {category.label}
              </button>
            );
          })}
          {/* Show All Button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <FileText className={`h-4 w-4 mr-2 ${
              activeCategory === 'all' ? 'text-white' : 'text-orange-600'
            }`} />
            <span>Show All</span>
          </button>
        </div>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Title of this entry"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* Emergency Phase Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Emergency Phase *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              >
                {Object.entries(categories).map(([key, category]) => (
                  <option key={key} value={key} className={category.color}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Removed Incident Name and Timestamp fields */}

            {/* Operational Period: Date/Time From and Date/Time To */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Operational Period - Date / Time from:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                      setFormData({...formData, date_from: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_from ? formData.date_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                        setFormData({...formData, date_from: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Date / Time to:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                      setFormData({...formData, date_to: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_to ? formData.date_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                        setFormData({...formData, date_to: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Situation Summary *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="a collective description of the current situation"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.description ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Priority and Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Name and Contact Phone */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={formData.contact_phone}
                  onChange={(e) => {
                    // Only allow digits and auto-format as XXX-XXX-XXXX
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) {
                      formatted = digits.slice(0, 3);
                      if (digits.length > 3) {
                        formatted += '-' + digits.slice(3, 6);
                      }
                      if (digits.length > 6) {
                        formatted += '-' + digits.slice(6, 10);
                      }
                    }
                    if (formatted.length <= 12) {
                      setFormData({...formData, contact_phone: formatted});
                    }
                  }}
                  placeholder="204-123-4567"
                  maxLength="12"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.contact_phone ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
              </div>
            </div>

            {/* Category-specific fields */}
            {categories[formData.category]?.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-white mb-1">
                  {field.label} {field.required && '*'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    rows={3}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                )}
                {errors[field.key] && <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>}
              </div>
            ))}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="notes / comments / remarks"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Add Entry')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Entries List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-orange-500">
            {activeCategory === 'all' ? 'All Entries' : categories[activeCategory]?.label + ' Entries'}
          </h3>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-white mt-2">Loading entries...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-orange-500 mb-2">
                {activeCategory === 'all' ? 'No Scenario Entries Found' : `No ${categories[activeCategory]?.label} Entries`}
              </h3>
              <p className="text-white mb-4">
                {activeCategory === 'all' 
                  ? 'Start by adding your first scenario entry in any category for this exercise.'
                  : `Start by adding your first ${categories[activeCategory]?.label.toLowerCase()} entry for this exercise.`
                }
              </p>
            </div>
          ) : (
            <SortableEntryList
              entries={filteredData}
              setEntries={(newEntries) => {
                if (activeCategory === 'all') {
                  setScenarioData(newEntries);
                } else {
                  const otherEntries = scenarioData.filter(d => d.category !== activeCategory);
                  setScenarioData([...otherEntries, ...newEntries]);
                }
              }}
              reorderEndpoint="scenario-entries/reorder"
              renderEntry={(entry) => {
                const priorityOption = priorityOptions.find(p => p.value === entry.priority);
                const statusOption = statusOptions.find(s => s.value === entry.status);
                
                return (
                  <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => toggleEntrySelection(entry.id)}
                          />
                          <h4 className="text-lg font-semibold text-white">{entry.title}</h4>
                          <Badge className={`${priorityOption?.color} bg-transparent border`}>
                            {priorityOption?.label}
                          </Badge>
                          <Badge className={`${statusOption?.color} bg-transparent border`}>
                            {statusOption?.label}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 mb-3">{entry.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
                          {(entry.date_from || entry.date_to) ? (
                            <div>
                              <span className="font-medium">Op Period:</span>{' '}
                              {entry.date_from ? entry.date_from.replace('T', ' ').slice(0, 16) : 'N/A'}
                              {' → '}
                              {entry.date_to ? entry.date_to.replace('T', ' ').slice(0, 16) : 'N/A'}
                            </div>
                          ) : entry.timestamp ? (
                            <div>
                              <span className="font-medium">Timestamp:</span> {format24HourTimestamp(entry.timestamp)}
                            </div>
                          ) : null}
                          {entry.location && (
                            <div>
                              <span className="font-medium">Location:</span> {entry.location}
                            </div>
                          )}
                          {entry.contact_info && (
                            <div>
                              <span className="font-medium">Contact:</span> {entry.contact_info}
                            </div>
                          )}
                        </div>
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-gray-600 rounded text-sm text-gray-300">
                            <span className="font-medium">Notes:</span> {entry.notes}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handlePrint(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Incident Objectives Module Component

export default ScenarioModule;
